if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        // Track when we're intentionally scrolling the thumbnails
        this.isScrollingThumbnails = false;
        // Add a debounce flag to prevent rapid successive clicks
        this.isProcessingClick = false;
        // Add a timestamp for the last click to measure delays
        this.lastClickTime = 0;
        if (!this.elements.thumbnails) return;

        // Listen for screen size changes to adjust thumbnail orientation
        this.mql.addEventListener('change', this.handleScreenSizeChange.bind(this));
        
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        
        // Add scroll event listener to handle mobile syncing
        this.elements.viewer.addEventListener('scroll', debounce(this.onMainSliderScroll.bind(this), 100));
        
        // Add touch events for better mobile handling
        if (this.elements.viewer.slider) {
          this.elements.viewer.slider.addEventListener('touchend', () => {
            // After touch ends, check which slide is most visible
            setTimeout(() => this.onMainSliderScroll(), 100);
          });
          
          // Also listen for regular click events on the main slider to ensure syncing
          this.elements.viewer.slider.addEventListener('click', () => {
            setTimeout(() => this.onMainSliderScroll(), 100);
          });
        }
        
        // Explicitly set up click handlers on all thumbnails
        this.setupThumbnailClickHandlers();
        
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        
        // Initialize proper orientation based on current screen size
        this.handleScreenSizeChange();
        
        // Initialize thumbnails by finding the current active slide
        this.initializeThumbnails();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      onMainSliderScroll() {
        // If we're currently in the middle of setting active media, don't interfere
        if (this._updatingActiveMedia || this._preventThumbnailReset || this.isProcessingClick) return;
        
        if (this.mql.matches) return; // Only use this on mobile
        
        // Find which slide is most visible in the viewport
        const sliderItems = this.elements.viewer.querySelectorAll('[data-media-id]');
        if (!sliderItems.length) return;
        
        const viewerRect = this.elements.viewer.getBoundingClientRect();
        const viewerCenter = viewerRect.left + viewerRect.width / 2;
        
        let closestItem = null;
        let closestDistance = Infinity;
        let closestVisibleArea = 0;
        
        sliderItems.forEach(item => {
          const itemRect = item.getBoundingClientRect();
          const itemCenter = itemRect.left + itemRect.width / 2;
          const distance = Math.abs(viewerCenter - itemCenter);
          
          // Calculate the visible area of the item
          const leftEdge = Math.max(itemRect.left, viewerRect.left);
          const rightEdge = Math.min(itemRect.right, viewerRect.right);
          const visibleWidth = Math.max(0, rightEdge - leftEdge);
          const visibleArea = visibleWidth / itemRect.width; // Percentage of item visible
          
          // Prioritize items that are more visible and closer to center
          if (visibleArea > 0.5 || (closestItem === null && visibleArea > 0)) {
            if (visibleArea > closestVisibleArea || 
                (visibleArea === closestVisibleArea && distance < closestDistance)) {
              closestDistance = distance;
              closestItem = item;
              closestVisibleArea = visibleArea;
            }
          }
        });
        
        if (closestItem && closestItem.dataset.mediaId) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${closestItem.dataset.mediaId}"]`
          );
          
          if (thumbnail) {
            // Make sure we don't reapply to the same thumbnail
            const currentActive = this.elements.thumbnails.querySelector('button[aria-current="true"]');
            const newActive = thumbnail.querySelector('button');
            
            if (!currentActive || currentActive !== newActive) {
              // Set a flag to prevent interference with thumbnail scrolling
              this._preventThumbnailReset = true;
              
              // Update thumbnail without scrolling
              this.setActiveThumbnail(thumbnail, true, true);
              
              // Clear the flag after a delay
              setTimeout(() => {
                this._preventThumbnailReset = false;
              }, 500);
            }
          }
        }
      }

      setActiveMedia(mediaId, prepend) {
        // Find the media element in the main carousel
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
           
        if (!activeMedia) {
          return;
        }
        
        // Set a flag to indicate we're in the middle of an active media change
        // This prevents other methods from interfering with our scrolling
        this._updatingActiveMedia = true;
        
        // Update active class on main carousel items
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        
        // Immediately scroll the main carousel to show the selected media
        // This is critical for thumbnail clicks to update the main view
        if (this.elements.viewer.slider && activeMedia) {
          // Force scrolling with immediate execution (no setTimeout)
          this.elements.viewer.slider.scrollTo({
            left: activeMedia.offsetLeft,
            behavior: this.isScrollingThumbnails ? 'smooth' : 'auto'
          });
        }
        
        // Additional processing with setTimeout for animation
        window.setTimeout(() => {
          // Page scrolling (if needed)
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
          
          // On mobile, ensure thumbnails sync after main scroll completes
          if (!this.mql.matches) {
            setTimeout(() => {
              // Only sync if we're not currently handling a thumbnail click
              if (!this.isProcessingClick) {
                this.onMainSliderScroll();
              }
              this._updatingActiveMedia = false;
            }, 200);
          } else {
            this._updatingActiveMedia = false;
          }
        });
        
        this.playActiveMedia(activeMedia);

        // Update thumbnails
        if (!this.elements.thumbnails) {
          this._updatingActiveMedia = false;
          return;
        }
        
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (activeThumbnail) {
          // Prevent the thumbnail scroll position from being changed again
          this._preventThumbnailReset = true;
          
          // Set the active thumbnail with custom flag to preserve scroll position
          this.setActiveThumbnail(activeThumbnail, false, true);
          
          // Clear the prevention flag after enough time has passed for transitions
          setTimeout(() => {
            this._preventThumbnailReset = false;
          }, 1000);
        }
        
        this.announceLiveRegion(activeMedia, activeThumbnail?.dataset.mediaPosition || '1');
      }

      setActiveThumbnail(thumbnail, fromScrolling = false, preserveScroll = false) {
        if (!this.elements.thumbnails || !thumbnail) return;

        // Just update the active state without scrolling if:
        // 1. We're already processing a thumbnail click on an edge item
        // 2. This call is from scrolling and we're on mobile
        // 3. We explicitly want to preserve scroll position
        const shouldPreserveScroll = 
          preserveScroll || 
          this._preventThumbnailReset || 
          (fromScrolling && !this.mql.matches);
        
        // Always update aria-current state
        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        
        // If we should preserve scroll position, exit early
        if (shouldPreserveScroll) {
          return;
        }
        
        // Get orientation and the slider element
        const orientation = this.elements.thumbnails.getAttribute('orientation');
        const thumbnailList = this.elements.thumbnails.slider;
        if (!thumbnailList) return;
        
        // Set flag to indicate we're intentionally scrolling
        this.isScrollingThumbnails = true;
        
        // For mobile, ensure we're always using horizontal scrolling
        const isMobile = !this.mql.matches;
        const effectiveOrientation = isMobile ? 'horizontal' : orientation;
        
        if (effectiveOrientation === 'vertical') {
          // VERTICAL ORIENTATION
          const thumbnailTop = thumbnail.offsetTop;
          const thumbnailBottom = thumbnailTop + thumbnail.clientHeight;
          const sliderTop = thumbnailList.scrollTop;
          const sliderBottom = sliderTop + thumbnailList.clientHeight;
          
          // Check if thumbnail is outside of the visible area
          const isAboveViewport = thumbnailTop < sliderTop;
          const isBelowViewport = thumbnailBottom > sliderBottom;
          
          // Check if thumbnail is at the edge of the visible area (first or last item)
          const isFirstVisible = thumbnailTop >= sliderTop && thumbnailTop < sliderTop + thumbnail.clientHeight;
          const isLastVisible = thumbnailBottom <= sliderBottom && thumbnailBottom > sliderBottom - thumbnail.clientHeight;
          
          if (isAboveViewport || isFirstVisible) {
            // BACKWARD SCROLLING (UP)
            // When scrolling backward, move back by approximately one viewport height
            const pageOffset = thumbnailList.clientHeight * 0.8; // 80% of viewport
            const targetPosition = Math.max(thumbnailTop - pageOffset, 0);
            thumbnailList.scrollTo({ top: targetPosition, behavior: 'smooth' });
          } else if (isBelowViewport || isLastVisible) {
            // FORWARD SCROLLING (DOWN)
            // Position the selected item at the top of the viewport
            thumbnailList.scrollTo({ top: thumbnailTop, behavior: 'smooth' });
          }
        } else {
          // HORIZONTAL ORIENTATION
          const thumbnailLeft = thumbnail.offsetLeft;
          const thumbnailRight = thumbnailLeft + thumbnail.clientWidth;
          const sliderLeft = thumbnailList.scrollLeft;
          const sliderRight = sliderLeft + thumbnailList.clientWidth;
          
          // Check if thumbnail is outside of the visible area
          const isLeftOfViewport = thumbnailLeft < sliderLeft;
          const isRightOfViewport = thumbnailRight > sliderRight;
          
          // Check if thumbnail is at the edge of the visible area (first or last item)
          const isFirstVisible = thumbnailLeft >= sliderLeft && thumbnailLeft < sliderLeft + thumbnail.clientWidth;
          const isLastVisible = thumbnailRight <= sliderRight && thumbnailRight > sliderRight - thumbnail.clientWidth;
          
          if (isLeftOfViewport || isFirstVisible) {
            // BACKWARD SCROLLING (LEFT)
            // When scrolling backward, move back by approximately one viewport width
            const pageOffset = thumbnailList.clientWidth * 0.8; // 80% of viewport
            const targetPosition = Math.max(thumbnailLeft - pageOffset, 0);
            thumbnailList.scrollTo({ left: targetPosition, behavior: 'smooth' });
          } else if (isRightOfViewport || isLastVisible) {
            // FORWARD SCROLLING (RIGHT)
            // Position the selected item at the left of the viewport
            thumbnailList.scrollTo({ left: thumbnailLeft, behavior: 'smooth' });
          }
        }
        
        // Reset the scrolling flag after a short delay (after animation completes)
        setTimeout(() => {
          this.isScrollingThumbnails = false;
        }, 500);
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      handleScreenSizeChange() {
        if (!this.elements.thumbnails) return;
        
        const wasMobile = this.elements.thumbnails.getAttribute('orientation') === 'horizontal' && 
                         this._originalOrientation === 'vertical';
        const isMobile = !this.mql.matches;
        
        // Store the original orientation if not already stored
        if (!this._originalOrientation && this.elements.thumbnails.hasAttribute('orientation')) {
          this._originalOrientation = this.elements.thumbnails.getAttribute('orientation');
        }
        
        // On mobile, force horizontal orientation
        if (isMobile) {
          this.elements.thumbnails.setAttribute('orientation', 'horizontal');
        } else if (this._originalOrientation) {
          // Restore original orientation on desktop
          this.elements.thumbnails.setAttribute('orientation', this._originalOrientation);
        }
        
        // Find current active media and sync thumbnails
        const activeMedia = this.elements.viewer.querySelector('.is-active') || 
                           this.elements.viewer.querySelector('[data-media-id]');
        if (activeMedia && activeMedia.dataset.mediaId) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${activeMedia.dataset.mediaId}"]`
          );
          if (thumbnail) {
            // Wait a short moment for layout calculations to complete
            setTimeout(() => {
              // If transitioning between mobile and desktop or vice versa,
              // ensure the thumbnails are properly synchronized
              if (wasMobile !== isMobile) {
                this.setupThumbnailClickHandlers();
              }
              
              // Force-update active thumbnail and scroll into view if needed
              this.setActiveThumbnail(thumbnail, false);
              
              // On mobile, run the main slider scroll handler to ensure sync
              if (isMobile) {
                // Ensure main slider's scroll position shows active media
                if (this.elements.viewer.slider) {
                  this.elements.viewer.slider.scrollTo({
                    left: activeMedia.offsetLeft,
                    behavior: 'auto'
                  });
                }
                
                setTimeout(() => this.onMainSliderScroll(), 100);
              }
            }, 50);
          }
        }
      }

      // Method to set up all thumbnail click handlers
      setupThumbnailClickHandlers() {
        if (!this.elements.thumbnails) return;
        
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          // Remove any existing click handlers to prevent duplicates
          const button = mediaToSwitch.querySelector('button');
          if (!button) return;
          
          // Clear existing click handlers by cloning and replacing
          const newButton = button.cloneNode(true);
          button.parentNode.replaceChild(newButton, button);
          
          // Add click handler that updates the main carousel
          newButton.addEventListener('click', (event) => {
            event.preventDefault();
            
            // Implement debounce mechanism to prevent rapid clicks
            const currentTime = Date.now();
            const timeSinceLastClick = currentTime - this.lastClickTime;
            
            // If we're already processing a click or it's too soon after the previous click, ignore this click
            if (this.isProcessingClick || timeSinceLastClick < 300) {
              return;
            }
            
            // Set the debounce flags
            this.isProcessingClick = true;
            this.lastClickTime = currentTime;
            
            // Get the mediaId from the parent's data-target attribute
            const mediaId = mediaToSwitch.dataset.target;
            
            // Get the thumbnail slider and other properties needed for scrolling
            const thumbnailList = this.elements.thumbnails.slider;
            if (!thumbnailList) {
              this.setActiveMedia(mediaId, false);
              // Reset the processing flag after a delay
              setTimeout(() => {
                this.isProcessingClick = false;
              }, 500);
              return;
            }
            
            // Determine if this is an edge item that should trigger scrolling
            const orientation = this.elements.thumbnails.getAttribute('orientation');
            const isMobile = !this.mql.matches;
            const effectiveOrientation = isMobile ? 'horizontal' : orientation;
            
            // Check if the clicked thumbnail is at an edge
            let isEdgeItem = false;
            let isFirstVisible = false;
            let isLastVisible = false;
            
            if (effectiveOrientation === 'horizontal') {
              // Horizontal orientation
              const thumbnailLeft = mediaToSwitch.offsetLeft;
              const thumbnailRight = thumbnailLeft + mediaToSwitch.clientWidth;
              const sliderLeft = thumbnailList.scrollLeft;
              const sliderRight = sliderLeft + thumbnailList.clientWidth;
              
              // Is this the first visible item?
              isFirstVisible = Math.abs(thumbnailLeft - sliderLeft) < mediaToSwitch.clientWidth;
              
              // Is this the last visible item?
              isLastVisible = Math.abs(thumbnailRight - sliderRight) < mediaToSwitch.clientWidth;
              
              // Edge item if it's the first or last visible
              isEdgeItem = isFirstVisible || isLastVisible;
            } else {
              // Vertical orientation
              const thumbnailTop = mediaToSwitch.offsetTop;
              const thumbnailBottom = thumbnailTop + mediaToSwitch.clientHeight;
              const sliderTop = thumbnailList.scrollTop;
              const sliderBottom = sliderTop + thumbnailList.clientHeight;
              
              // Is this the first visible item?
              isFirstVisible = Math.abs(thumbnailTop - sliderTop) < mediaToSwitch.clientHeight;
              
              // Is this the last visible item?
              isLastVisible = Math.abs(thumbnailBottom - sliderBottom) < mediaToSwitch.clientHeight;
              
              // Edge item if it's the first or last visible
              isEdgeItem = isFirstVisible || isLastVisible;
            }
            
            // First update the active thumbnail visually
            this._preventThumbnailReset = true;
            const buttons = this.elements.thumbnails.querySelectorAll('button');
            buttons.forEach(btn => btn.removeAttribute('aria-current'));
            newButton.setAttribute('aria-current', true);
            
            // If it's an edge item, perform the appropriate scrolling
            if (isEdgeItem) {
              if (isFirstVisible) {
                // First visible item - scroll backward
                if (effectiveOrientation === 'horizontal') {
                  // Calculate how many items to show before this one
                  const pageOffset = thumbnailList.clientWidth * 0.8;
                  const targetPosition = Math.max(mediaToSwitch.offsetLeft - pageOffset, 0);
                  thumbnailList.scrollTo({ left: targetPosition, behavior: 'smooth' });
                } else {
                  // Vertical orientation
                  const pageOffset = thumbnailList.clientHeight * 0.8;
                  const targetPosition = Math.max(mediaToSwitch.offsetTop - pageOffset, 0);
                  thumbnailList.scrollTo({ top: targetPosition, behavior: 'smooth' });
                }
              } else if (isLastVisible) {
                // Last visible item - scroll forward
                if (effectiveOrientation === 'horizontal') {
                  // Position this item at the left edge
                  thumbnailList.scrollTo({ left: mediaToSwitch.offsetLeft, behavior: 'smooth' });
                } else {
                  // Vertical orientation - position at top
                  thumbnailList.scrollTo({ top: mediaToSwitch.offsetTop, behavior: 'smooth' });
                }
              }
              
              // Longer delay for edge items to allow scrolling to complete
              setTimeout(() => {
                this._preventThumbnailReset = false;
                this.setActiveMedia(mediaId, false);
                
                // Reset the processing flag after scrolling completes
                setTimeout(() => {
                  this.isProcessingClick = false;
                }, 300);
              }, 400);
            } else {
              // For non-edge items, update more quickly
              setTimeout(() => {
                this._preventThumbnailReset = false;
                this.setActiveMedia(mediaId, false);
                
                // Reset the processing flag
                setTimeout(() => {
                  this.isProcessingClick = false;
                }, 300);
              }, 50);
            }
          });
        });
      }

      // Method to initialize thumbnails based on current active slide
      initializeThumbnails() {
        if (!this.elements.thumbnails || !this.elements.viewer) return;
        
        // Find active media (or first media item if none is active)
        const activeMedia = 
          this.elements.viewer.querySelector('[data-media-id].is-active') || 
          this.elements.viewer.querySelector('[data-media-id]');
        
        if (activeMedia && activeMedia.dataset.mediaId) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${activeMedia.dataset.mediaId}"]`
          );
          
          if (thumbnail) {
            // Ensure this thumbnail is active and visible
            this.setActiveThumbnail(thumbnail);
          }
        }
      }
      
      // Method to reinitialize the gallery
      reinitialize() {
        // Refresh all event handlers
        this.setupThumbnailClickHandlers();
        
        // Re-apply the proper orientation based on screen size
        this.handleScreenSizeChange();
        
        // Sync thumbnails with main carousel
        this.initializeThumbnails();
      }
    }
  );
}
